import React, { useState } from 'react';
import { Col, Row, Typography } from 'antd';
import {
  Link,
  BaseButton,
  ButtonGroup,
  Currency,
  Divider,
  Drawer,
  HistoryDropDown,
  LoadingState,
  Modal,
  TimeWithTimeZone,
} from '@common/components';
import moment from 'moment';

import { useMutation, useQuery } from '@common/hooks';
import { DeleteHourBlock, ReviewHourBlockInfo } from '@timekeeping/graphql';
import {
  EMPTY_PLACEHOLDER,
  formatDateInTz,
  formatSecondsToDuration,
  formatSummaryDate,
  parseInTz,
} from '@timekeeping/services';
import { createMessage } from 'works/legacy/lib/message-helper';
import { buildDialogUrl } from '@common/services';

const { Text, Title } = Typography;

export const HourBlockReviewDrawer = ({
  open,
  onClose,
  hourBlockId = '',
}: {
  open: boolean;
  onClose: () => void;
  hourBlockId?: string;
}) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const { loading, data } = useQuery(ReviewHourBlockInfo, {
    skip: !hourBlockId,
    variables: { id: hourBlockId },
  });

  const [performDeleteAction] = useMutation(DeleteHourBlock, {
    onCompleted: () => {
      createMessage('success', 'Hours deleted.');
      setDeleteLoading(false);
      setConfirmVisible(false);
    },
    onError: () => {
      createMessage(
        'error',
        'An error occurred while trying to delete hours. Please, try again.'
      );
      setDeleteLoading(false);
      setConfirmVisible(false);
    },
  });

  const onDeleteConfirm = () => {
    setDeleteLoading(true);
    performDeleteAction({
      variables: { id: hourBlockId! },
    });
  };

  const hourBlock = data?.hourBlockInfo?.data;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={
          hourBlock
            ? formatSummaryDate(
                parseInTz(
                  hourBlock.inOut.startedAt!,
                  hourBlock.inOut.startedAtTz
                )
              )
            : undefined
        }
        footer={
          <ButtonGroup>
            {hourBlock?.canSoftDelete.value &&
              hourBlock?.state !== 'deleted' && (
                <BaseButton
                  className="hour-block-drawer-delete-button color-coral"
                  size="large"
                  type="ghost"
                  onClick={() => setConfirmVisible(true)}
                  disabled={loading}
                >
                  Delete
                </BaseButton>
              )}
            {hourBlock?.workerTimesheet.canManagerUpsertHours.value &&
              hourBlock?.state !== 'deleted' && (
                <Link
                  to={buildDialogUrl('timekeeping-hour-block-edit-drawer', {
                    props: { hourBlockId },
                    overlap: true,
                  })}
                >
                  <BaseButton
                    className="hour-block-drawer-edit-button"
                    size="large"
                    type="primary"
                    disabled={loading}
                  >
                    Edit
                  </BaseButton>
                </Link>
              )}
            {hourBlock?.workerTimesheet.canManagerUpsertHours.value &&
              hourBlock?.state === 'deleted' && (
                <Link
                  to={buildDialogUrl('timekeeping-hour-block-restore-drawer', {
                    props: { hourBlockId },
                    overlap: true,
                  })}
                >
                  <BaseButton
                    className="hour-block-drawer-restore-button color-elm"
                    size="large"
                    type="ghost"
                    disabled={loading}
                  >
                    Restore
                  </BaseButton>
                </Link>
              )}
          </ButtonGroup>
        }
      >
        <LoadingState loading={loading} />
        {hourBlock && (
          <Row gutter={[0, 18]}>
            <Col span={24}>
              <Row gutter={[0, 4]}>
                <Col span={24}>
                  <Title level={4}>Location</Title>
                </Col>
                <Col span={24}>
                  <Row gutter={[0, 12]}>
                    <Col span={6}>
                      <Text type="secondary">Facility</Text>
                    </Col>
                    <Col span={18}>{hourBlock.facility!.name}</Col>
                    <Col span={6}>
                      <Text type="secondary">Unit</Text>
                    </Col>
                    <Col span={18}>{hourBlock.unit!.name}</Col>
                    <Col span={6}>
                      <Text type="secondary">Cost Center</Text>
                    </Col>
                    <Col span={18}>{hourBlock.unit!.costCenter}</Col>
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Divider />
            </Col>
            <Col span={24}>
              <Row gutter={[0, 4]}>
                <Col span={24}>
                  <Title level={4}>Hours</Title>
                </Col>
                <Col span={24}>
                  <Row gutter={[0, 12]}>
                    <Col span={6}>
                      <Text type="secondary">Date Worked</Text>
                    </Col>
                    <Col span={18}>
                      <TimeWithTimeZone
                        date={parseInTz(
                          hourBlock.inOut.startedAt!,
                          hourBlock.inOut.startedAtTz
                        )}
                        variant="short_format"
                      />
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">Hour Type</Text>
                    </Col>
                    <Col span={18}>
                      <Text>{hourBlock.hourType!.name}</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">Clock In</Text>
                    </Col>
                    <Col span={18}>
                      <Text>
                        {formatDateInTz(
                          hourBlock.inOut.startedAt!,
                          hourBlock.inOut.startedAtTz
                        )}
                      </Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">Clock Out</Text>
                    </Col>
                    <Col span={18}>
                      <Text>
                        {formatDateInTz(
                          hourBlock.inOut.endedAt!,
                          hourBlock.inOut.endedAtTz
                        )}
                      </Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">Break Start</Text>
                    </Col>
                    <Col span={18}>
                      <Text>
                        {hourBlock.lunchBreak
                          ? formatDateInTz(
                              hourBlock.lunchBreak.startedAt!,
                              hourBlock.lunchBreak.startedAtTz
                            )
                          : EMPTY_PLACEHOLDER}
                      </Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">Break End</Text>
                    </Col>
                    <Col span={18}>
                      <Text>
                        {hourBlock.lunchBreak
                          ? formatDateInTz(
                              hourBlock.lunchBreak.endedAt!,
                              hourBlock.lunchBreak.endedAtTz
                            )
                          : EMPTY_PLACEHOLDER}
                      </Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">Total Time</Text>
                    </Col>
                    <Col span={18}>
                      {formatSecondsToDuration(hourBlock.totalSeconds)}
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">Rate</Text>
                    </Col>
                    <Col span={18}>
                      {hourBlock.billingRate ? (
                        <Currency value={hourBlock.billingRate} />
                      ) : (
                        EMPTY_PLACEHOLDER
                      )}
                    </Col>
                    {hourBlock.inOut.startedAtTz && (
                      <Col span={24}>
                        <Text>
                          *All times in{' '}
                          {moment.tz(hourBlock.inOut.startedAtTz).format('z')}
                        </Text>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Col>
            {hourBlock.notes && (
              <>
                <Col span={24}>
                  <Divider />
                </Col>
                <Col span={24}>
                  <Row gutter={[0, 8]}>
                    <Col span={24}>
                      <Title level={4}>Worker Note</Title>
                    </Col>
                    <Col span={24}>
                      <Text>{hourBlock.notes}</Text>
                    </Col>
                  </Row>
                </Col>
              </>
            )}
            <Col span={24}>
              <HistoryDropDown data={hourBlock.history || []} />
            </Col>
          </Row>
        )}
      </Drawer>
      <Modal
        visible={confirmVisible}
        title="Are you sure you want to delete?"
        onCancel={() => setConfirmVisible(false)}
        footer={
          <>
            <BaseButton
              type="ghost"
              className="hour-block-delete-action-cancel color-elm"
              onClick={() => setConfirmVisible(false)}
              disabled={deleteLoading}
            >
              No, Cancel
            </BaseButton>
            <BaseButton
              type="primary"
              className="hour-block-delete-action-confirm"
              onClick={onDeleteConfirm}
              loading={deleteLoading}
            >
              Yes, Delete
            </BaseButton>
          </>
        }
      >
        You&apos;ll be able to undo deleting up until you approve this
        timesheet.
      </Modal>
    </>
  );
};
